import { useState } from "react";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errMsg = (err) => err.response ? (apiError(err.response.data?.detail) || `Errore (${err.response.status})`)
  : err.request ? "Impossibile contattare il server. Controlla la connessione e riprova." : (err.message || "Errore imprevisto");

export default function AuthCard({ mode, setMode, refParam = "" }) {
  const { login, register, loginGuest } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client", referral_code: refParam.toUpperCase() });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const enterGuest = async () => {
    setBusy(true);
    try { await loginGuest(); } catch { toast.error("Accesso ospite non disponibile. Riprova."); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        setSent(true); toast.success(t("forgot_sent"));
      } else if (mode === "login") {
        await login(form.email, form.password); toast.success(t("welcome"));
      } else {
        await register({ name: form.name, email: form.email, password: form.password, role: form.role, referral_code: form.referral_code || undefined });
        toast.success(t("welcome"));
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const tab = (m, label, testid) => (
    <button type="button" data-testid={testid} role="tab" aria-selected={mode === m} onClick={() => { setMode(m); setSent(false); }}
      className={`flex-1 h-11 rounded-full text-sm transition-all ${mode === m ? "bg-black text-white font-bold shadow-md" : "text-neutral-500 font-medium hover:text-black hover:bg-white/60"}`}>{label}</button>
  );

  return (
    <div data-testid="auth-card" className="rounded-3xl border border-neutral-200 p-6 sm:p-8 shadow-sm bg-white">
      {mode === "forgot" ? (
        <div className="mb-5">
          <h3 data-testid="forgot-title" className="font-display font-bold text-base md:text-lg">{t("forgot_title")}</h3>
          <p className="text-xs text-neutral-500 mt-1">{t("forgot_desc")}</p>
        </div>
      ) : (
        <div role="tablist" className="flex gap-1 mb-6 bg-neutral-100 rounded-full p-1">
          {tab("login", t("access"), "tab-login")}{tab("register", t("register"), "tab-register")}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <div><Label className="text-xs">{t("name")}</Label><Input data-testid="input-name" value={form.name} onChange={set("name")} required className="mt-1 h-11" /></div>
        )}
        <div><Label className="text-xs">{t("email")}</Label><Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} required className="mt-1 h-11" placeholder="nome@email.com" /></div>
        {mode !== "forgot" && (
          <div>
            <div className="flex items-center justify-between"><Label className="text-xs">{t("password")}</Label>
              {mode === "login" && <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="text-xs text-neutral-500 underline underline-offset-2 hover:text-black">{t("forgot")}</button>}
            </div>
            <Input data-testid="input-password" type="password" value={form.password} onChange={set("password")} required minLength={6} className="mt-1 h-11" />
          </div>
        )}
        {mode === "register" && (
          <>
            <div>
              <Label className="text-xs">{t("role")}</Label>
              <div data-testid="select-role" role="radiogroup" className="mt-1 grid grid-cols-2 gap-2">
                {["client", "specialist", "promoter", "owner"].map((r) => (
                  <button key={r} type="button" role="radio" aria-checked={form.role === r} data-testid={`role-${r}`} onClick={() => setForm((f) => ({ ...f, role: r }))}
                    className={`h-11 rounded-full text-sm font-medium border transition-colors ${form.role === r ? "bg-black text-white border-black" : "bg-white text-neutral-700 border-neutral-200 hover:border-black"}`}>{t(`role_${r}`)}</button>
                ))}
              </div>
            </div>
            <div><Label className="text-xs">{t("referral_optional")}</Label><Input data-testid="input-referral" value={form.referral_code} onChange={set("referral_code")} className="mt-1 h-11" placeholder="ENBISSY" /></div>
          </>
        )}
        {sent && mode === "forgot" && <p data-testid="forgot-sent" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl p-3">{t("forgot_sent")}</p>}
        <Button data-testid="submit-auth-btn" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-black text-white hover:bg-neutral-800 text-base font-semibold">
          {busy ? "…" : mode === "login" ? t("access") : mode === "forgot" ? t("forgot_send") : t("create_account")}
        </Button>
        {mode === "forgot" && <button type="button" data-testid="back-to-login" onClick={() => { setMode("login"); setSent(false); }} className="w-full text-xs text-neutral-500 underline underline-offset-2">{t("back_login")}</button>}
      </form>
      {mode !== "forgot" && (
        <>
          <button type="button" data-testid="guest-login-btn" onClick={enterGuest} disabled={busy}
            className="mt-5 w-full flex items-center justify-center gap-2 h-11 rounded-full border border-neutral-200 text-sm font-medium text-neutral-700 hover:border-black transition-colors">
            <Eye size={16} /> {t("guest_btn")}
          </button>
          <p data-testid="guest-hint" className="mt-2 text-[11px] text-neutral-500 text-center leading-snug">{t("guest_hint")}</p>
        </>
      )}
    </div>
  );
}
